import { useEffect, useState } from "react";
import { getStudioLeads, type DerivedLead } from "./studioFromGraph";

/* Live leads hook — prefers `/api/leads` (which proxies to the
 * in-cluster agent, encrypted Pages-only) and falls back to the
 * typed in-browser derivation if the agent isn't reachable.
 *
 * No silent stubs. The `source` flag tells callers whether they're
 * seeing live agent data or the typed-derivation fallback so the
 * cockpit can show a small "live" / "derived" badge if needed. */

export interface LeadsResult {
  leads: readonly DerivedLead[];
  source: "agent" | "fallback";
  reason?: string;
}

export function useStudioLeadsLive(): LeadsResult {
  // First paint = synchronous typed derivation. The agent fetch
  // upgrades it asynchronously when the response lands.
  const [state, setState] = useState<LeadsResult>(() => ({
    leads: getStudioLeads(),
    source: "fallback",
    reason: "first paint",
  }));

  useEffect(() => {
    let cancelled = false;
    async function pull(): Promise<void> {
      try {
        const r = await fetch("/api/leads", { credentials: "include" });
        if (!r.ok) return;
        const body = (await r.json()) as { leads?: DerivedLead[]; source?: "agent" | "fallback"; reason?: string };
        if (cancelled) return;
        if (body.source === "agent" && body.leads && body.leads.length > 0) {
          setState({ leads: body.leads, source: "agent" });
        } else if (body.source === "fallback") {
          setState({ leads: getStudioLeads(), source: "fallback", ...(body.reason ? { reason: body.reason } : {}) });
        }
      } catch {
        // Network blip — keep the typed derivation. No silent zero.
      }
    }
    void pull();
    function onProjectChanged(): void { setState({ leads: getStudioLeads(), source: "fallback" }); void pull(); }
    window.addEventListener("foyrmind:projectChanged", onProjectChanged);
    return () => {
      cancelled = true;
      window.removeEventListener("foyrmind:projectChanged", onProjectChanged);
    };
  }, []);

  return state;
}
